// oxlint-disable react/only-export-components
import { cva, type VariantProps } from "class-variance-authority"
import { Tabs as TabsPrimitive } from "radix-ui"
import type { ComponentProps, ReactNode } from "react"
import { cn } from "cn"
import { Badge } from "../badge/badge"

const tabsListVariants = cva(
  "inline-flex items-center gap-0 rounded-full p-1 text-labels-secondary",
  {
    variants: {
      // Figma-Achse `Variant`: „on white" / „on grey" → kebab-case. Bestimmt
      // nur den Hintergrund der Leiste (je nach Untergrund der Seite).
      variant: {
        "on-white": "bg-neutrals-v2-200",
        "on-grey": "bg-neutrals-v2-300",
      },
    },
    defaultVariants: {
      variant: "on-white",
    },
  }
)

export type TabsVariant = NonNullable<
  VariantProps<typeof tabsListVariants>["variant"]
>

function Tabs({
  className,
  ...props
}: ComponentProps<typeof TabsPrimitive.Root>) {
  return (
    <TabsPrimitive.Root
      data-slot="tabs"
      className={cn("flex flex-col gap-4", className)}
      {...props}
    />
  )
}

export type TabsListProps = ComponentProps<typeof TabsPrimitive.List> &
  VariantProps<typeof tabsListVariants>

function TabsList({ className, variant, ...props }: TabsListProps) {
  return (
    <TabsPrimitive.List
      data-slot="tabs-list"
      data-variant={variant ?? "on-white"}
      className={cn(tabsListVariants({ variant }), className)}
      {...props}
    />
  )
}

export type TabsTriggerProps = ComponentProps<typeof TabsPrimitive.Trigger> & {
  /** Figma `showIcon` + `icon`: Icon vor dem Label (18 px). Gesetzt = sichtbar. */
  icon?: ReactNode
  /** Figma `showBadge`: Badge-Inhalt (z. B. Anzahl) hinter dem Label. Gesetzt = sichtbar. */
  badge?: ReactNode
}

/**
 * Figma-Achse `state` (selected/unselected) wird auf Radix' `data-state`
 * (`active`/`inactive`) abgebildet, nicht als eigene Prop.
 */
function TabsTrigger({
  className,
  children,
  icon,
  badge,
  ...props
}: TabsTriggerProps) {
  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      className={cn(
        "inline-flex h-7 shrink-0 cursor-pointer items-center justify-center gap-1 rounded-full px-3 py-1 text-sm leading-5 font-semibold whitespace-nowrap text-labels-secondary transition-colors outline-none",
        "focus-visible:ring-2 focus-visible:ring-neutrals-v2-1000 focus-visible:ring-offset-2",
        "data-[state=active]:bg-backgrounds-primary data-[state=active]:text-labels-primary",
        "disabled:cursor-not-allowed disabled:opacity-50",
        "[&_svg]:pointer-events-none [&_svg]:size-[18px] [&_svg]:shrink-0",
        className
      )}
      {...props}
    >
      {icon}
      {children}
      {badge != null && badge !== false && (
        <Badge size="xs" data-slot="tabs-trigger-badge">
          {badge}
        </Badge>
      )}
    </TabsPrimitive.Trigger>
  )
}

function TabsContent({
  className,
  ...props
}: ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      data-slot="tabs-content"
      className={cn("outline-none", className)}
      {...props}
    />
  )
}

export { Tabs, TabsContent, TabsList, TabsTrigger, tabsListVariants }
